<template>
	<span v-if="false" />
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { popupService } from '@arritech/ui/Popup';
import { toastSuccess, toastError } from '@arritech/ui/Toast';
import { SessionExpiringPromptPanel } from '@/components/UIComponents/SessionExpiringPromptPanel';

const sessionPromptPopupId = 'session-expiring-prompt-popup';

const authStore = useAuthStore();
const route = useRoute();
const { t } = useI18n();

const isVisible = ref(false);
const timeLeft = ref(0);
const timerInterval = ref<ReturnType<typeof setInterval> | null>(null);
const checkInterval = ref<ReturnType<typeof setInterval> | null>(null);
const isRefreshing = ref(false);
const originalTitle = ref(document.title);
const titleInterval = ref<ReturnType<typeof setInterval> | null>(null);
const isTabActive = ref(true);
const hasIgnored = ref(false);

const isNoAuthRoute = computed(() => {
	return route.matched.some((record) => Boolean(record.meta?.requiresNoAuth));
});

const formattedTime = computed(() => {
	if (timeLeft.value <= 0) return '00:00';
	const minutes = Math.floor(timeLeft.value / 60000);
	const seconds = Math.floor((timeLeft.value % 60000) / 1000);
	return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

function isPromptPopupOpen() {
	return popupService.popups.value.some(
		(popup) => popup.id === sessionPromptPopupId
	);
}

function updatePromptPopup() {
	if (!isPromptPopupOpen()) {
		return;
	}

	popupService.updatePopup(sessionPromptPopupId, {
		props: {
			formattedTime: formattedTime.value,
			isRefreshing: isRefreshing.value,
			onIgnore: ignorePrompt,
			onStayLoggedIn: stayLoggedIn,
		},
	});
}

function showPrompt() {
	if (isPromptPopupOpen()) {
		isVisible.value = true;
		updatePromptPopup();
		return;
	}

	popupService.showPopup({
		id: sessionPromptPopupId,
		title: t('auth.sessionExpiring.title'),
		component: SessionExpiringPromptPanel,
		config: {
			background: true,
			position: 'center',
			canClose: false,
			width: '450px',
		},
		props: {
			formattedTime: formattedTime.value,
			isRefreshing: isRefreshing.value,
			onIgnore: ignorePrompt,
			onStayLoggedIn: stayLoggedIn,
		},
		onClose: () => {
			isVisible.value = false;
			stopTitleFlash();
		},
	});

	isVisible.value = true;
}

function startCountdown() {
	if (timerInterval.value) clearInterval(timerInterval.value);
	timerInterval.value = setInterval(() => {
		const expiryDate = Date.parse(authStore.tokenExpiryDate);
		if (!isNaN(expiryDate)) {
			timeLeft.value = expiryDate - Date.now();
			if (timeLeft.value <= 0) {
				closePrompt();
				return;
			}
			updatePromptPopup();
		}
	}, 1000);
}

async function stayLoggedIn() {
	if (isRefreshing.value) return;
	isRefreshing.value = true;
	updatePromptPopup();

	try {
		const response = await authStore.refreshToken();
		if (response) {
			toastSuccess({
				message: t('auth.sessionExpiring.extendedSuccess'),
			});
			hasIgnored.value = false;
			closePrompt();
		} else {
			toastError({ message: t('errors.extendSession') });
		}
	} catch (error) {
		console.error(t('auth.sessionExpiring.extendSessionError'), error);
		toastError({ message: t('auth.sessionExpiring.extendSessionError') });
	} finally {
		isRefreshing.value = false;
		updatePromptPopup();
	}
}

function closePrompt() {
	isVisible.value = false;

	if (timerInterval.value) {
		clearInterval(timerInterval.value);
		timerInterval.value = null;
	}

	if (isPromptPopupOpen()) {
		popupService.close({ id: sessionPromptPopupId });
	}

	stopTitleFlash();
}

function ignorePrompt() {
	hasIgnored.value = true;
	closePrompt();
}

function checkSessionExpiry() {
	if (isNoAuthRoute.value || !authStore.isAuthenticated()) {
		closePrompt();
		return;
	}

	let timeUntilExpiry = 0;
	const expiryDate = Date.parse(authStore.tokenExpiryDate);
	if (!isNaN(expiryDate)) {
		timeUntilExpiry = expiryDate - Date.now();
	}

	if (timeUntilExpiry > 0 && timeUntilExpiry <= 300000 && !hasIgnored.value) {
		timeLeft.value = timeUntilExpiry;
		if (!isVisible.value) {
			showPrompt();
			startCountdown();
			if (!isTabActive.value) {
				startTitleFlash();
			}
		} else {
			updatePromptPopup();
		}
	} else if (timeUntilExpiry > 300000) {
		hasIgnored.value = false;
		if (isVisible.value) {
			closePrompt();
		}
	} else if (timeUntilExpiry <= 0) {
		closePrompt();
	}
}

function handleVisibilityChange() {
	isTabActive.value = document.visibilityState === 'visible';
	if (isTabActive.value) {
		stopTitleFlash();
	} else if (isVisible.value) {
		startTitleFlash();
	}
}

function startTitleFlash() {
	if (titleInterval.value) return;
	let showOriginal = false;
	titleInterval.value = setInterval(() => {
		document.title = showOriginal
			? originalTitle.value
			: `⚠️ ${t('auth.sessionExpiring.tabTitleAlert')}`;
		showOriginal = !showOriginal;
	}, 1000);
}

function stopTitleFlash() {
	if (titleInterval.value) {
		clearInterval(titleInterval.value);
		titleInterval.value = null;
	}
	document.title = originalTitle.value;
}

watch(
	() => route.fullPath,
	() => {
		if (isNoAuthRoute.value) {
			closePrompt();
			return;
		}
		checkSessionExpiry();
	}
);

onMounted(() => {
	originalTitle.value = document.title;
	checkSessionExpiry();
	checkInterval.value = setInterval(() => checkSessionExpiry(), 10000);
	document.addEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
	if (checkInterval.value) clearInterval(checkInterval.value);
	if (timerInterval.value) clearInterval(timerInterval.value);
	document.removeEventListener('visibilitychange', handleVisibilityChange);
	if (isPromptPopupOpen()) {
		popupService.close({ id: sessionPromptPopupId });
	}
	stopTitleFlash();
});
</script>
